import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import Icon from '../common/containers/icon';
import buildDefaultSelection, { buildPendingSelection } from '../../utils/cabinetCsvExportSelection';
const buildColumnsMapFromFormTypes = function buildColumnsMapFromFormTypes() {
  let formTypes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return formTypes.reduce((acc, formType) => {
    var _formType$columns;
    if ((_formType$columns = formType.columns) !== null && _formType$columns !== void 0 && _formType$columns.length) {
      acc[formType.formTypeIdentifier] = formType.columns;
    }
    return acc;
  }, {});
};
const computeHasAnyExportableColumns = (formTypes, columnsByFormType) => formTypes.some(formType => {
  const columns = columnsByFormType[formType.formTypeIdentifier] || formType.columns || [];
  return columns.length > 0;
});
const computeIsExportReady = (selection, columnsByFormType, loadingIdentifiers) => {
  const selectedIds = Object.keys(selection);
  if (!selectedIds.length) {
    return false;
  }
  if (selectedIds.some(formTypeIdentifier => loadingIdentifiers.has(formTypeIdentifier))) {
    return false;
  }
  return selectedIds.every(formTypeIdentifier => {
    const columns = columnsByFormType[formTypeIdentifier];
    const paths = selection[formTypeIdentifier];
    return Array.isArray(columns) && columns.length > 0 && Array.isArray(paths) && paths.length > 0;
  });
};
const applyLoadedColumnsToSelection = (currentSelection, formTypeIdentifiers, columnsByFormType) => {
  const nextSelection = _objectSpread({}, currentSelection);
  formTypeIdentifiers.forEach(formTypeIdentifier => {
    if (!Object.prototype.hasOwnProperty.call(nextSelection, formTypeIdentifier)) {
      return;
    }
    const columns = columnsByFormType[formTypeIdentifier] || [];
    if (columns.length) {
      nextSelection[formTypeIdentifier] = columns.map(column => column.contentPath);
    }
  });
  return nextSelection;
};
const CabinetCsvExportColumnModal = _ref => {
  let _ref$formTypes = _ref.formTypes,
    formTypes = _ref$formTypes === void 0 ? [] : _ref$formTypes,
    _ref$totalCount = _ref.totalCount,
    totalCount = _ref$totalCount === void 0 ? 0 : _ref$totalCount,
    _ref$selectionRef = _ref.selectionRef,
    selectionRef = _ref$selectionRef === void 0 ? null : _ref$selectionRef,
    _ref$validationError = _ref.validationError,
    validationError = _ref$validationError === void 0 ? '' : _ref$validationError,
    _ref$loadColumnOption = _ref.loadColumnOptions,
    loadColumnOptions = _ref$loadColumnOption === void 0 ? null : _ref$loadColumnOption,
    _ref$onExportReadyCha = _ref.onExportReadyChange,
    onExportReadyChange = _ref$onExportReadyCha === void 0 ? null : _ref$onExportReadyCha;
  const lazyLoadColumns = typeof loadColumnOptions === 'function';
  const _useState = useState(() => {
      if (lazyLoadColumns) {
        return (selectionRef === null || selectionRef === void 0 ? void 0 : selectionRef.columnsByFormType) || {};
      }
      return buildColumnsMapFromFormTypes(formTypes);
    }),
    _useState2 = _slicedToArray(_useState, 2),
    columnsByFormType = _useState2[0],
    setColumnsByFormType = _useState2[1];
  const _useState3 = useState(() => new Set()),
    _useState4 = _slicedToArray(_useState3, 2),
    loadingIdentifiers = _useState4[0],
    setLoadingIdentifiers = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    loadError = _useState6[0],
    setLoadError = _useState6[1];
  const columnsByFormTypeRef = useRef(columnsByFormType);
  const loadingIdentifiersRef = useRef(loadingIdentifiers);
  useEffect(() => {
    columnsByFormTypeRef.current = columnsByFormType;
    if (selectionRef) {
      Object.assign(selectionRef, {
        columnsByFormType
      });
    }
  }, [columnsByFormType, selectionRef]);
  const defaultSelection = useMemo(() => {
    if (lazyLoadColumns) {
      return buildPendingSelection(formTypes);
    }
    return buildDefaultSelection(formTypes, columnsByFormType);
  }, [columnsByFormType, formTypes, lazyLoadColumns]);
  const getInitialSelection = () => {
    if (!selectionRef) {
      return defaultSelection;
    }
    const refSelection = selectionRef.selection;
    if (refSelection && typeof refSelection === 'object' && Object.keys(refSelection).length > 0) {
      return refSelection;
    }
    if (selectionRef.validationError) {
      return refSelection || {};
    }
    return defaultSelection;
  };
  const _useState7 = useState(getInitialSelection),
    _useState8 = _slicedToArray(_useState7, 2),
    selection = _useState8[0],
    setSelection = _useState8[1];
  const _useState9 = useState(() => new Set()),
    _useState0 = _slicedToArray(_useState9, 2),
    expandedFormTypes = _useState0[0],
    setExpandedFormTypes = _useState0[1];
  useEffect(() => {
    loadingIdentifiersRef.current = loadingIdentifiers;
  }, [loadingIdentifiers]);
  const syncSelectionRef = useCallback(function (nextSelection, nextLoadingIdentifiers) {
    let formTypesForColumns = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : formTypes;
    if (!selectionRef) {
      return;
    }
    const columnsMap = columnsByFormTypeRef.current;
    const isLoadingColumns = nextLoadingIdentifiers.size > 0;
    const hasAnyExportableColumns = computeHasAnyExportableColumns(formTypesForColumns, columnsMap);
    const hasNoExportableColumns = !isLoadingColumns && !hasAnyExportableColumns;
    const isExportReady = computeIsExportReady(nextSelection, columnsMap, nextLoadingIdentifiers);
    const wasExportReady = selectionRef.isExportReady;
    const wasLoadingColumns = selectionRef.isLoadingColumns;
    const wasNoExportableColumns = selectionRef.hasNoExportableColumns;
    Object.assign(selectionRef, {
      selection: nextSelection,
      isExportReady,
      isLoadingColumns,
      hasNoExportableColumns
    });
    const exportStateChanged = wasExportReady !== isExportReady || wasLoadingColumns !== isLoadingColumns || wasNoExportableColumns !== hasNoExportableColumns;
    if (typeof onExportReadyChange === 'function' && exportStateChanged) {
      onExportReadyChange();
    }
  }, [formTypes, onExportReadyChange, selectionRef]);
  const ensureColumnsLoaded = useCallback(async function () {
    let formTypeIdentifiers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    if (!lazyLoadColumns) {
      return;
    }
    const identifiersToLoad = formTypeIdentifiers.filter(formTypeIdentifier => !columnsByFormTypeRef.current[formTypeIdentifier] && !loadingIdentifiersRef.current.has(formTypeIdentifier));
    if (!identifiersToLoad.length) {
      return;
    }
    setLoadError('');
    setLoadingIdentifiers(current => {
      const next = new Set(current);
      identifiersToLoad.forEach(formTypeIdentifier => next.add(formTypeIdentifier));
      loadingIdentifiersRef.current = next;
      return next;
    });
    try {
      const result = await loadColumnOptions(identifiersToLoad);
      if (!(result !== null && result !== void 0 && result.success)) {
        throw new Error((result === null || result === void 0 ? void 0 : result.message) || 'Unable to load export columns.');
      }
      const loadedColumns = result.columnOptionsByFormType || {};
      const normalizedLoadedColumns = identifiersToLoad.reduce((acc, formTypeIdentifier) => {
        acc[formTypeIdentifier] = loadedColumns[formTypeIdentifier] || [];
        return acc;
      }, {});
      setColumnsByFormType(current => {
        const next = _objectSpread(_objectSpread({}, current), normalizedLoadedColumns);
        columnsByFormTypeRef.current = next;
        return next;
      });
      const nextLoading = new Set(loadingIdentifiersRef.current);
      identifiersToLoad.forEach(formTypeIdentifier => nextLoading.delete(formTypeIdentifier));
      loadingIdentifiersRef.current = nextLoading;
      setLoadingIdentifiers(nextLoading);
      setSelection(current => {
        const nextSelection = applyLoadedColumnsToSelection(current, identifiersToLoad, normalizedLoadedColumns);
        syncSelectionRef(nextSelection, nextLoading);
        return nextSelection;
      });
    } catch (error) {
      setLoadError((error === null || error === void 0 ? void 0 : error.message) || 'Unable to load export columns.');
      setLoadingIdentifiers(current => {
        const next = new Set(current);
        identifiersToLoad.forEach(formTypeIdentifier => next.delete(formTypeIdentifier));
        loadingIdentifiersRef.current = next;
        syncSelectionRef((selectionRef === null || selectionRef === void 0 ? void 0 : selectionRef.selection) || {}, next);
        return next;
      });
    }
  }, [lazyLoadColumns, loadColumnOptions, selectionRef, syncSelectionRef]);
  useEffect(() => {
    if (!lazyLoadColumns) {
      return;
    }
    ensureColumnsLoaded(formTypes.map(formType => formType.formTypeIdentifier));
  }, [ensureColumnsLoaded, formTypes, lazyLoadColumns]);
  useEffect(() => {
    if (selectionRef) {
      syncSelectionRef(selection, loadingIdentifiers);
    }
  }, [loadingIdentifiers, selection, selectionRef, syncSelectionRef]);
  useEffect(() => {
    if (selectionRef) {
      return;
    }
    setSelection(defaultSelection);
    setExpandedFormTypes(new Set());
  }, [defaultSelection, selectionRef]);
  const selectedFormTypeCount = useMemo(() => Object.keys(selection).length, [selection]);
  const selectedColumnCount = useMemo(() => Object.values(selection).reduce((sum, paths) => sum + (Array.isArray(paths) ? paths.length : 0), 0), [selection]);
  const allSelected = selectedFormTypeCount === formTypes.length && formTypes.every(formType => {
    const columns = columnsByFormType[formType.formTypeIdentifier] || [];
    return (selection[formType.formTypeIdentifier] || []).length === columns.length && columns.length > 0;
  });
  const effectiveValidationError = selectionRef ? selectionRef.validationError || '' : validationError;
  const isLoadingColumns = loadingIdentifiers.size > 0;
  const hasAnyExportableColumns = computeHasAnyExportableColumns(formTypes, columnsByFormType);
  const hasNoExportableColumns = lazyLoadColumns ? !isLoadingColumns && !hasAnyExportableColumns : !hasAnyExportableColumns;
  const clearValidationError = () => {
    if (selectionRef) {
      Object.assign(selectionRef, {
        validationError: ''
      });
    }
  };
  const toggleExpanded = formTypeIdentifier => {
    setExpandedFormTypes(current => {
      const next = new Set(current);
      if (next.has(formTypeIdentifier)) {
        next.delete(formTypeIdentifier);
      } else {
        next.add(formTypeIdentifier);
      }
      return next;
    });
    if (lazyLoadColumns) {
      ensureColumnsLoaded([formTypeIdentifier]);
    }
  };
  const toggleFormType = formType => {
    clearValidationError();
    const formTypeIdentifier = formType.formTypeIdentifier;
    const columns = columnsByFormType[formTypeIdentifier] || [];
    const isSelected = Object.prototype.hasOwnProperty.call(selection, formTypeIdentifier);
    if (isSelected) {
      setSelection(current => {
        const next = _objectSpread({}, current);
        delete next[formTypeIdentifier];
        return next;
      });
      setExpandedFormTypes(current => {
        const next = new Set(current);
        next.delete(formTypeIdentifier);
        return next;
      });
      return;
    }
    const columnPaths = columns.map(column => column.contentPath);
    setSelection(current => _objectSpread(_objectSpread({}, current), {}, {
      [formTypeIdentifier]: columnPaths
    }));
    setExpandedFormTypes(current => new Set(current).add(formTypeIdentifier));
    if (lazyLoadColumns && !columns.length) {
      ensureColumnsLoaded([formTypeIdentifier]);
    }
  };
  const toggleColumn = (formTypeIdentifier, contentPath, allPaths) => {
    clearValidationError();
    setSelection(current => {
      const currentPaths = current[formTypeIdentifier] || [];
      const nextPaths = currentPaths.includes(contentPath) ? currentPaths.filter(path => path !== contentPath) : [...currentPaths, contentPath];
      if (!nextPaths.length) {
        const next = _objectSpread({}, current);
        delete next[formTypeIdentifier];
        return next;
      }
      return _objectSpread(_objectSpread({}, current), {}, {
        [formTypeIdentifier]: nextPaths.length === allPaths.length ? allPaths : nextPaths
      });
    });
  };
  const selectAll = () => {
    clearValidationError();
    setExpandedFormTypes(new Set());
    const columnsMap = lazyLoadColumns ? columnsByFormTypeRef.current : columnsByFormType;
    setSelection(buildDefaultSelection(formTypes, columnsMap));
    if (lazyLoadColumns) {
      ensureColumnsLoaded(formTypes.map(formType => formType.formTypeIdentifier));
    }
  };
  const clearAll = () => {
    clearValidationError();
    setSelection({});
    setExpandedFormTypes(new Set());
  };
  const showSelectColumnsGuidance = !effectiveValidationError && !loadError && !hasNoExportableColumns && selectedColumnCount === 0 && !isLoadingColumns;
  return /*#__PURE__*/React.createElement("div", {
    className: "t-cabinet-csv-export-column-modal"
  }, /*#__PURE__*/React.createElement("p", null, "Select asset types and columns to include in the export.", ' ', /*#__PURE__*/React.createElement("span", {
    style: {
      whiteSpace: 'nowrap'
    }
  }, "(", totalCount, ' ', "form", totalCount === 1 ? '' : 's', ")")), lazyLoadColumns && isLoadingColumns && !loadError ? /*#__PURE__*/React.createElement("p", {
    className: "text-muted",
    style: {
      marginBottom: '8px'
    }
  }, "Loading column options...") : null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '8px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    color: "link",
    size: "sm",
    className: "p-0",
    style: {
      marginRight: '12px'
    },
    onClick: selectAll,
    disabled: allSelected
  }, "Select all"), /*#__PURE__*/React.createElement(Button, {
    color: "link",
    size: "sm",
    className: "p-0",
    onClick: clearAll,
    disabled: selectedFormTypeCount === 0
  }, "Clear all")), /*#__PURE__*/React.createElement("div", {
    style: {
      maxHeight: '400px',
      overflowY: 'auto',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      padding: '8px 12px'
    }
  }, formTypes.map(formType => {
    const formTypeIdentifier = formType.formTypeIdentifier,
      formTypeName = formType.formTypeName,
      count = formType.count;
    const columns = columnsByFormType[formTypeIdentifier] || [];
    const isFormTypeSelected = Object.prototype.hasOwnProperty.call(selection, formTypeIdentifier);
    const selectedPaths = selection[formTypeIdentifier] || [];
    const selectedPathSet = new Set(selectedPaths);
    const isExpanded = expandedFormTypes.has(formTypeIdentifier);
    const glyph = isExpanded ? 'caret-down fa-2x' : 'caret-left fa-2x';
    const allPaths = columns.map(column => column.contentPath);
    const formTypeInputId = "cabinet-csv-export-form-type-".concat(formTypeIdentifier);
    const isLoadingFormTypeColumns = loadingIdentifiers.has(formTypeIdentifier);
    return /*#__PURE__*/React.createElement("div", {
      key: formTypeIdentifier,
      style: {
        marginBottom: '10px',
        borderBottom: '1px solid #eee',
        paddingBottom: '8px'
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center'
      }
    }, /*#__PURE__*/React.createElement("label", {
      htmlFor: formTypeInputId,
      style: {
        marginBottom: 0,
        cursor: 'pointer',
        flex: 1
      }
    }, /*#__PURE__*/React.createElement("input", {
      id: formTypeInputId,
      type: "checkbox",
      checked: isFormTypeSelected,
      onChange: () => toggleFormType(formType),
      style: {
        marginRight: '8px'
      }
    }), /*#__PURE__*/React.createElement("strong", null, formTypeName || formTypeIdentifier), ' ', /*#__PURE__*/React.createElement("span", {
      style: {
        color: '#6c757d'
      }
    }, "(", count, ' ', "form", count === 1 ? '' : 's', ")")), /*#__PURE__*/React.createElement(Button, {
      color: "link",
      size: "sm",
      className: "p-0",
      "aria-expanded": isExpanded,
      "aria-label": "".concat(isExpanded ? 'Collapse' : 'Expand', " columns for ").concat(formTypeName || formTypeIdentifier),
      onClick: () => toggleExpanded(formTypeIdentifier)
    }, /*#__PURE__*/React.createElement(Icon, {
      style: {
        position: 'relative',
        top: 10
      },
      glyph: glyph
    }))), isExpanded ? /*#__PURE__*/React.createElement("div", {
      style: {
        marginLeft: '24px',
        marginTop: '6px'
      }
    }, isLoadingFormTypeColumns ? /*#__PURE__*/React.createElement("p", {
      className: "text-muted",
      style: {
        marginBottom: 0
      }
    }, "Loading columns...") : null, !isLoadingFormTypeColumns && !columns.length ? /*#__PURE__*/React.createElement("p", {
      className: "text-muted",
      style: {
        marginBottom: 0
      }
    }, "No exportable columns found.") : null, !isLoadingFormTypeColumns ? columns.map(column => {
      const columnInputId = "cabinet-csv-export-column-".concat(formTypeIdentifier, "-").concat(column.contentPath.replace(/[^a-zA-Z0-9_-]/g, '-'));
      return /*#__PURE__*/React.createElement("label", {
        key: "".concat(formTypeIdentifier, "-").concat(column.contentPath),
        htmlFor: columnInputId,
        style: {
          display: 'block',
          marginBottom: '4px',
          cursor: 'pointer'
        }
      }, /*#__PURE__*/React.createElement("input", {
        id: columnInputId,
        type: "checkbox",
        checked: selectedPathSet.has(column.contentPath),
        onChange: () => toggleColumn(formTypeIdentifier, column.contentPath, allPaths),
        style: {
          marginRight: '8px'
        }
      }), column.header);
    }) : null) : null);
  })), loadError ? /*#__PURE__*/React.createElement("p", {
    className: "text-danger",
    style: {
      marginTop: '12px',
      marginBottom: 0
    }
  }, loadError) : null, effectiveValidationError ? /*#__PURE__*/React.createElement("p", {
    className: "text-danger",
    style: {
      marginTop: '12px',
      marginBottom: 0
    }
  }, effectiveValidationError) : null, !effectiveValidationError && !loadError && hasNoExportableColumns ? /*#__PURE__*/React.createElement("p", {
    className: "text-muted",
    style: {
      marginTop: '12px',
      marginBottom: 0
    }
  }, "No exportable columns found.") : null, showSelectColumnsGuidance ? /*#__PURE__*/React.createElement("p", {
    className: "text-muted",
    style: {
      marginTop: '12px',
      marginBottom: 0
    }
  }, "Select at least one asset type and column to export.") : null);
};
CabinetCsvExportColumnModal.propTypes = {
  formTypes: PropTypes.arrayOf(PropTypes.shape({
    formTypeIdentifier: PropTypes.string.isRequired,
    formTypeName: PropTypes.string,
    count: PropTypes.number,
    columns: PropTypes.arrayOf(PropTypes.shape({
      contentPath: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      fieldType: PropTypes.string
    }))
  })),
  totalCount: PropTypes.number,
  selectionRef: PropTypes.shape({
    selection: PropTypes.shape({}),
    validationError: PropTypes.string,
    isExportReady: PropTypes.bool,
    isLoadingColumns: PropTypes.bool,
    hasNoExportableColumns: PropTypes.bool,
    columnsByFormType: PropTypes.shape({})
  }),
  validationError: PropTypes.string,
  loadColumnOptions: PropTypes.func,
  onExportReadyChange: PropTypes.func
};
export default CabinetCsvExportColumnModal;